import { daysBetween } from './date';
import type { ScheduleCell, ScheduleCellAssignment } from './types';

/**
 * One assignment as a continuous Gantt bar across the visible window.
 *
 * The API is day-expanded — a five-day assignment arrives as five identical items on five
 * dates — so the grid used to draw five separate chips. A bar collapses those back into the
 * single thing they represent, using the assignment's own `plannedStart`/`plannedEnd` rather
 * than inferring a range from which cells happen to be populated.
 */
export interface ScheduleBar {
  assignment: ScheduleCellAssignment;
  /** First visible day column, inclusive. */
  startIndex: number;
  /** Last visible day column, inclusive. */
  endIndex: number;
  /** Vertical slot within the row; overlapping bars get different lanes. */
  lane: number;
  /** The assignment starts before the window — draw a flat, open left edge. */
  continuesBefore: boolean;
  /** The assignment ends after the window — draw a flat, open right edge. */
  continuesAfter: boolean;
}

/**
 * Collapse a person's day-expanded cells into laned bars.
 *
 * `days` must be the contiguous, ascending day list the grid renders.
 */
export function buildBars(cells: ScheduleCell[], days: string[]): ScheduleBar[] {
  if (days.length === 0) return [];

  const windowStart = days[0];
  const windowEnd = days[days.length - 1];
  const lastIndex = days.length - 1;

  // One entry per assignment, not per day. Sorting by id keeps lane assignment stable between
  // renders so bars don't jump lanes when the window scrolls.
  const seen = new Map<number, ScheduleCellAssignment>();
  for (const cell of cells) {
    for (const item of cell.items) {
      if (!seen.has(item.assignmentId)) seen.set(item.assignmentId, item);
    }
  }

  const bars: Omit<ScheduleBar, 'lane'>[] = [];
  for (const assignment of seen.values()) {
    // Guard against a malformed range rather than rendering a bar that extends leftward.
    if (!assignment.plannedStart || !assignment.plannedEnd) continue;
    if (assignment.plannedEnd < assignment.plannedStart) continue;

    const rawStart = daysBetween(windowStart, assignment.plannedStart);
    const rawEnd = daysBetween(windowStart, assignment.plannedEnd);

    // Entirely outside the window — shouldn't happen given the query, but a stale cache can
    // hand us one mid-navigation.
    if (rawEnd < 0 || rawStart > lastIndex) continue;

    bars.push({
      assignment,
      startIndex: Math.max(0, rawStart),
      endIndex: Math.min(lastIndex, rawEnd),
      continuesBefore: assignment.plannedStart < windowStart,
      continuesAfter: assignment.plannedEnd > windowEnd,
    });
  }

  return packIntoLanes(bars);
}

/**
 * Greedy interval packing: each bar drops into the first lane whose previous bar has already
 * ended. Overlapping assignments — a tech double-booked, or a travel day inside a job — stack
 * instead of colliding.
 *
 * Sorted by start, then by longer-first, so the longest run in a cluster takes the top lane and
 * the eye follows one continuous line across the window.
 */
function packIntoLanes(bars: Omit<ScheduleBar, 'lane'>[]): ScheduleBar[] {
  const sorted = [...bars].sort((a, b) => {
    if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
    const aLen = a.endIndex - a.startIndex;
    const bLen = b.endIndex - b.startIndex;
    if (aLen !== bLen) return bLen - aLen;
    return a.assignment.assignmentId - b.assignment.assignmentId;
  });

  // laneEnds[i] = last occupied column in lane i.
  const laneEnds: number[] = [];
  return sorted.map((bar) => {
    let lane = laneEnds.findIndex((end) => end < bar.startIndex);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(bar.endIndex);
    } else {
      laneEnds[lane] = bar.endIndex;
    }
    return { ...bar, lane };
  });
}

/** How many lanes a set of bars needs. Drives row height. */
export function laneCount(bars: ScheduleBar[]): number {
  return bars.reduce((max, b) => Math.max(max, b.lane + 1), 0);
}
